// Admin Control Center UI kit — same visual identity as V2 (dark glass + gold)
import { useState, useEffect } from "react";
import { X, Upload, Loader2, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { GlassCard, CountUp, Chip, Bar, Skeleton } from "@/v2/components/ui";
import { uploadFile } from "@/v2/lib/files";

export { GlassCard, CountUp, Chip, Bar, Skeleton };

export const tip = {
  contentStyle: { background: "#1e2d37", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, color: "#f6f8fa", fontSize: 12 },
  labelStyle: { color: "#bcc8cf" },
};

export const AV_FALLBACK = (e) => {
  e.currentTarget.src = "https://api.dicebear.com/9.x/initials/svg?seed=User&backgroundColor=0A4D3C";
};

export const PageHead = ({ icon: I, title, sub, children }) => (
  <div className="flex items-end justify-between flex-wrap gap-3">
    <div>
      <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2">
        {I && <I className="text-[#91cab8]" />} {title}
      </h1>
      {sub && <p className="text-[#bcc8cf] mt-1 text-sm">{sub}</p>}
    </div>
    {children}
  </div>
);

export const Modal = ({ title, onClose, children, wide = false, testId = "v2-modal" }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="v2-overlay flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className={`v2-glass v2-glass-gold w-full ${wide ? "max-w-3xl" : "max-w-lg"} my-8 p-6`}
        onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-extrabold">{title}</h3>
          <button onClick={onClose} data-testid={`${testId}-close`}
            className="w-9 h-9 rounded-full v2-inset flex items-center justify-center hover:opacity-80"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const Confirm = ({ title, body, confirmLabel = "تأكيد", danger = false, onConfirm, onClose, testId = "v2-confirm" }) => (
  <Modal title={title} onClose={onClose} testId={testId}>
    <p className="text-[#bcc8cf] text-sm mb-6 leading-relaxed">{body}</p>
    <div className="flex gap-3">
      <button onClick={onConfirm} data-testid={`${testId}-yes`} className={danger ? "v2-btn-danger flex-1" : "v2-btn flex-1"}>{confirmLabel}</button>
      <button onClick={onClose} data-testid={`${testId}-no`} className="v2-btn-ghost flex-1">إلغاء</button>
    </div>
  </Modal>
);

export const Toggle = ({ checked, onChange, label, testId }) => (
  <div className="flex items-center justify-between gap-3 v2-inset p-3">
    <span className="text-xs font-bold">{label}</span>
    <button type="button" onClick={() => onChange(!checked)} data-testid={testId}
      className="relative w-11 h-6 rounded-full transition-colors shrink-0"
      style={{ background: checked ? "#91cab8" : "rgba(255,255,255,0.14)" }}>
      <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
        style={{ right: checked ? "2px" : "22px" }} />
    </button>
  </div>
);

export const Table = ({ head, children, min = 720, testId }) => (
  <GlassCard className="overflow-x-auto" data-testid={testId}>
    <table className="v2-table" style={{ minWidth: min }}>
      <thead><tr>{head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </GlassCard>
);

export const Kpi = ({ icon: I, label, value, suffix = "", tone = "emerald", testId }) => {
  const tones = {
    emerald: ["#91cab8", "rgba(145,202,184,0.14)"],
    gold: ["#e3c799", "rgba(187,150,97,0.14)"],
    red: ["#F7A08A", "rgba(180,83,9,0.16)"],
    muted: ["#bcc8cf", "rgba(255,255,255,0.06)"],
  };
  const [c, bg] = tones[tone] || tones.emerald;
  return (
    <GlassCard hover className="p-4 sm:p-5" data-testid={testId}>
      <div className="w-10 h-10 rounded-2xl flex items-center justify-center mb-3" style={{ color: c, background: bg }}>
        <I size={20} strokeWidth={1.8} />
      </div>
      <div className="text-2xl font-extrabold"><CountUp value={value} />{suffix}</div>
      <div className="text-xs text-[#bcc8cf] mt-0.5">{label}</div>
    </GlassCard>
  );
};

export const Pagination = ({ page, pages, onChange }) => pages > 1 ? (
  <div className="flex items-center justify-center gap-3">
    <button onClick={() => onChange(Math.max(1, page - 1))} disabled={page === 1}
      data-testid="v2-page-prev" className="v2-btn-ghost v2-btn-sm disabled:opacity-40"><ChevronRight size={15} /> السابق</button>
    <span className="text-sm text-[#bcc8cf]">صفحة {page} من {pages}</span>
    <button onClick={() => onChange(Math.min(pages, page + 1))} disabled={page === pages}
      data-testid="v2-page-next" className="v2-btn-ghost v2-btn-sm disabled:opacity-40">التالي <ChevronLeft size={15} /></button>
  </div>
) : null;

export const UploadBtn = ({ accept, label = "رفع ملف", onDone, testId, className = "v2-btn-ghost v2-btn-sm" }) => {
  const [pct, setPct] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPct(0);
    try {
      const res = await uploadFile(file, setPct);
      toast.success("تم رفع الملف بنجاح");
      onDone?.(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || "تعذّر رفع الملف");
    } finally { setPct(null); }
  };
  return (
    <label className={`${className} cursor-pointer inline-flex items-center gap-2`} data-testid={testId}>
      {pct === null ? <><Upload size={14} /> {label}</> : <><Loader2 size={14} className="animate-spin" /> {pct}%</>}
      <input type="file" accept={accept} onChange={pick} className="hidden" disabled={pct !== null} />
    </label>
  );
};

export const Empty = ({ text }) => (
  <GlassCard className="p-10 text-center text-[#bcc8cf] text-sm">{text}</GlassCard>
);

export const EMP_TYPES = [
  { key: "field", label: "موظف ميداني" },
  { key: "office", label: "موظف مكتبي" },
  { key: "manager", label: "مدير إدارة" },
];

/** الجمهور المستهدف — فارغ = الجميع */
export const AudiencePicker = ({ value = [], onChange, testId = "audience" }) => {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {EMP_TYPES.map((t) => {
        const on = value.includes(t.key);
        return (
          <button key={t.key} type="button" onClick={() => toggle(t.key)} data-testid={`${testId}-${t.key}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
              on ? "text-[#25343f] bg-gradient-to-l from-[#e3c799] to-[#bb9661] border-transparent"
                : "text-[#bcc8cf] bg-white/5 border-white/10 hover:text-white"}`}>
            {t.label}
          </button>
        );
      })}
      <button type="button" onClick={() => onChange([])} data-testid={`${testId}-all`}
        className={`text-xs font-bold px-3 py-1.5 rounded-full border ${
          value.length === 0 ? "text-[#91cab8] bg-[#91cab8]/12 border-[#91cab8]/30" : "text-[#bcc8cf] bg-white/5 border-white/10"}`}>
        الجميع
      </button>
    </div>
  );
};

export const AudienceChips = ({ value = [] }) =>
  value.length === 0 ? <Chip tone="emerald">الجميع</Chip> : (
    <span className="inline-flex gap-1 flex-wrap">
      {value.map((v) => <Chip key={v} tone="gold">{(EMP_TYPES.find((t) => t.key === v) || {}).label || v}</Chip>)}
    </span>
  );
